import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import type { AvatarMode } from '@/app/hooks/useSaxaMode';

const MODE_OPTIONS: { value: AvatarMode; label: string }[] = [
  { value: 'ai', label: 'AI Core' },
  { value: 'anime', label: 'Anime Eyes' },
  { value: 'plain', label: 'Plain Emoji' },
  { value: 'manga', label: 'Manga' },
  { value: 'runes', label: 'Runes' },
];

interface ModeSwitcherProps {
  mode: AvatarMode;
  onModeChange: (mode: AvatarMode) => void;
}

export function ModeSwitcher({ mode, onModeChange }: ModeSwitcherProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const current = MODE_OPTIONS.find((o) => o.value === mode)?.label ?? 'Anime Eyes';

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-[10px] text-white/70 hover:text-white transition-colors"
      >
        {current}
        <ChevronDown size={10} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white dark:bg-[#0F1F3D] rounded-lg shadow-lg border border-gray-200 dark:border-[#1E3A8A]/30 py-1 z-50 min-w-[120px]">
          {MODE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => {
                onModeChange(opt.value);
                setOpen(false);
              }}
              className={`block w-full text-left px-3 py-1.5 text-xs transition-colors ${
                opt.value === mode
                  ? 'text-[#60A5FA] font-medium bg-[#60A5FA]/10'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#1E3A8A]/20'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
